<script>
    import Button from "../Button.svelte";
    import CustomCheckbox from "../CustomCheckbox.svelte";
    import FlatCard from "../FlatCard.svelte";
    import HorizontalRule from "../presentational/HorizontalRule.svelte";
    import { processNewCredentials } from "../../utilities/identity.js";
    import { createEventDispatcher, onMount } from "svelte";
    import { updateEmail, updateEmailConfirm } from "../../api/client.js";
    const dispatch = createEventDispatcher();

    // data the card displays
    export let data;

    // if the card cannot be edited and is only displaying the email.
    export let isReadOnly;

    let code,
        activeStepIdx = 0,
        emailResponse = {},
        steps = [],
        emailInputField,
        editedEmailAddress;

    $: email = data.emails.filter((email) => email.primary)[0] || {};
    $: newsletterOptIn = email.newsletter;

    const updateChecked = () => {
        newsletterOptIn = !newsletterOptIn;
        dispatch("message", {
            action: "UPDATE",
            data: {
                newsletter: newsletterOptIn,
            },
        });
    };

    /*
    TODO abstract to a "SteppedCard" component maybe.

        Cards (data-step=)
        0 Please enter your e-mail address so we can send you a code.
        1 Please enter the code we sent to your e-mail address (in #0).
        2 Please re-enter your code that we sent (in #0).
        3 Email not assoc. with account
    */
    const navigateToIndex = (newIdx) => {
        // move the display to the card with index newIdx (if newIdx is valid.)
        if (activeStepIdx === newIdx || !Number.isInteger(+newIdx) || newIdx < 0 || newIdx >= steps.length) return;
        // navigate and update active idx
        steps[activeStepIdx].classList.add("hidden");
        steps[newIdx].classList.remove("hidden");
        activeStepIdx = newIdx;

        if (activeStepIdx === 1 && emailInputField) {
            emailInputField.focus();
        }
    };

    const handleEmailSubmit = () => {
        updateEmail({
            dataToUpdate: { email: editedEmailAddress },
            onSuccess: function ({ data }) {
                emailResponse = data;
                console.log(data);
                navigateToIndex(2);
            },
            onError: function (err) {
                console.log(err);
            },
        });
    };

    // handleCodeSubmit sends the code the user enters that was e-mailed to their inbox
    // to the server, along with the initial e-mail response. The initial e-mail response
    // contains a nonce -- xsrf token such that you cannot simply guess at codes.
    const handleCodeSubmit = () => {
        updateEmailConfirm({
            dataToUpdate: { email: editedEmailAddress, auth_code: code, auth_id: emailResponse.nonce },
            statusHandlers: {
                "200": ({ data }) => {
                    processNewCredentials(data);
                    navigateToIndex(0);
                    dispatch("message", {
                        // TODO have more granular message than this that updates ALL the state.
                        action: "STATE_UPDATED",
                        data: {},
                    });
                },
                "400": function () {
                    // code error
                    navigateToIndex(3);
                },
                "401": function () {
                    navigateToIndex(3);
                },
                "409": function () {
                    // email verified but already in use
                    navigateToIndex(4);
                },
            },
        });
    };

    onMount(() => {
        editedEmailAddress = email.address;
    });

    import { t } from "../../i18n";
</script>

<!--src/components/View.svelte-->
<FlatCard>
    {#if isReadOnly}
        <div class="row collapse-to-col card-content" data-step="0" bind:this="{steps[0]}">
            <h2>{$t('contact_card')}</h2>
            <HorizontalRule />
            {#if email.address}
                <div class="row align-items-center justify-content-flex-start">
                    <i class="material-icons-outlined">mail</i>
                    <div class="flex-1 underline">{email.address}</div>
                </div>
                <div class="row no-wrap align-items-center justify-content-flex-start">
                    <CustomCheckbox isSelected="{newsletterOptIn}" on:click="{() => updateChecked()}" />
                    <div class="flex-1 clickable" on:click="{() => updateChecked()}">{$t('receive_updates')}</div>
                </div>
            {/if}
        </div>
    {:else}
        <div class="row collapse-to-col card-content hidden" data-step="1" bind:this="{steps[1]}">
            <h2>Contact</h2>
            <HorizontalRule />
            <form on:submit|preventDefault="{handleEmailSubmit}">
                <div class="row align-items-center justify-content-flex-start">
                    <input
                        bind:this="{emailInputField}"
                        bind:value="{editedEmailAddress}"
                        placeholder="{email.address}"
                        type="email"
                        name="email"
                        minlength="3"
                        required
                        aria-label="Email Address"
                        class="solid-silver-border rounded-edge"
                        spellcheck="false"
                    />
                    <div class="row align-items-center flex-1">
                        <Button flex="{2}" buttonText="{'Update Email'}" />
                        <i class="material-icons" on:click|preventDefault="{() => navigateToIndex(0)}">close</i>
                    </div>
                </div>
            </form>
            <HorizontalRule />
            <div class="row">
                Enter your updated email address and we'll send you an account access code. Your account won't be
                updated until you've entered the code.
            </div>
        </div>
        <div class="card-content hidden" data-step="2" bind:this="{steps[2]}">
            <h2>Enter Code</h2>
            <HorizontalRule />
            <div class="row align-items-center justify-content-flex-start">
                We just sent a code to {editedEmailAddress}. Enter the 6 digit code here.
            </div>
            <form on:submit|preventDefault="{handleCodeSubmit}">
                <div class="row align-items-center justify-content-flex-start collapse-to-col">
                    <input
                        bind:value="{code}"
                        placeholder="------"
                        type="text"
                        pattern="^[0-9]+$"
                        name="email-code"
                        minlength="6"
                        maxlength="6"
                        required
                        aria-label="Verification Code Sent to Your E-mail"
                        class="solid-silver-border rounded-edge code"
                    />
                    <div class="row align-items-center flex-1">
                        <Button buttonText="{$t('submit')}" flex="{2}" />
                        <i class="material-icons" on:click|preventDefault="{() => navigateToIndex(0)}">close</i>
                    </div>
                </div>
            </form>
            <HorizontalRule />
            <div class="row">
                Didn't get the code?
                <a href="/" on:click|preventDefault="{() => navigateToIndex(1)}">Enter your email address again</a>
                and we will resend it.
            </div>
        </div>
        <div class="card-content hidden" data-step="3" bind:this="{steps[3]}">
            <h2>Code Error</h2>
            <HorizontalRule />
            <div class="row align-items-center justify-content-flex-start">
                The code you entered isn't what we expected. Please enter the 6 digit code again.
            </div>
            <form on:submit|preventDefault="{handleCodeSubmit}">
                <div class="row align-items-center justify-content-flex-start collapse-to-col">
                    <input
                        bind:value="{code}"
                        placeholder="------"
                        type="text"
                        pattern="^[0-9]+$"
                        name="email-code"
                        minlength="6"
                        maxlength="6"
                        required
                        aria-label="Verification Code Sent to Your E-mail"
                        class="solid-silver-border rounded-edge code"
                    />
                    <div class="row align-items-center flex-1">
                        <Button buttonText="{$t('submit')}" flex="{2}" />
                        <i class="material-icons" on:click|preventDefault="{() => navigateToIndex(0)}">close</i>
                    </div>
                </div>
            </form>
            <HorizontalRule />
            <div class="row">
                <strong>Still having issues?</strong>
                <a href="/" on:click|preventDefault="{() => navigateToIndex(1)}">Enter your email address again</a>
                and we will resend it, or
                <a href="https://vpnsupport.getadblock.com/" target="_blank" rel="noopener noreferrer">contact support</a>
                .
            </div>
        </div>
        <div class="card-content hidden" data-step="4" bind:this="{steps[4]}">
            <h2>Contact</h2>
            <HorizontalRule />
            <div class="row align-items-center justify-content-flex-start">
                <i class="material-icons-outlined">mail</i>
                <div class="row flex-0-1-auto underline">{email.address}</div>
            </div>
            <div class="row align-items-center justify-content-flex-start flex-1">
                <Button classNames="{'secondary far-right'}" buttonText="{'Contact Support'}" flex="{2}" />
                <Button
                    classNames="{'far-right-auto'}"
                    buttonText="{'Try Again'}"
                    flex="{2}"
                    on:click="{() => navigateToIndex(1)}"
                />
            </div>
            <div class="row alert rounded-edge">
                <i class="material-icons">error</i>
                That email is already associated with an AdBlock account.
            </div>
        </div>
    {/if}
</FlatCard>

<style>
    input {
        margin-right: 24px;
    }
    div.card-content div {
        margin: 8px 0px;
        padding: 4px 0px;
        word-break: break-word;
    }
    div.card-content div.underline {
        padding-left: 8px;
        margin-left: 8px;
    }
    div.alert {
        background-color: var(--primaryColor);
        color: var(--backgroundColorLightest);
        padding: 8px;
    }

    .clickable {
        cursor: pointer;
        user-select: none;
    }
</style>
